import omit from 'lodash/omit';

import axios from '../../app/axios';
import { endpoints } from '../../utils/constants';

import ProposalEntity from '../../db/entities/proposal/ProposalEntity';
import ProposalDC from '../../db/entities/proposal/ProposalDC';
import { mapProposalEntityToDC, PriceCalculationRequestDC, mapPriceCalculationEntityToDC } from '../../db/entities/proposal/mapper';

import type { ProposalItemEntity, DISCOUNT_TYPES } from '../../db/entities/proposal/ProposalEntity';
import ProposalSettingsEntity from '../../db/entities/proposalSettings/ProposalSettingsEntity';
import { mapProposalSettingsEntityToDC } from '../../db/entities/proposalSettings/mapper';
import RequestHeaders from '../../enums/RequestHeaders';
import ContentTypes from '../../enums/ContentTypes';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';


interface PriceCalculationResponseDC {
  total: number;
  subtotal: number;
  subtotal_with_discount: number;
  tax: number;
}

interface PriceResult {
  total: number;
  subtotal: number;
  subtotalWithDiscount: number;
  tax: number;
}

async function getPrice(
    params: {
      items: ProposalItemEntity[];
      discountType: DISCOUNT_TYPES;
      discountAmount: number;
      includesTax: boolean;
      taxRate: number;
    }
): Promise<PriceResult> {
  const dc: PriceCalculationRequestDC = mapPriceCalculationEntityToDC(params);
  const response = await axios.post<PriceCalculationResponseDC>(endpoints.proposals.price, dc);

  const {
    total,
    subtotal,
    subtotal_with_discount: subtotalWithDiscount,
    tax
  } = response.data;

  return { total, subtotal, subtotalWithDiscount, tax };
}

type ProposalTotals = Pick<
  ProposalEntity,
  'subtotal_price' | 'price_with_discount' | 'tax_value' | 'price_with_tax'
>;

// The totals the server calculated, or undefined when the response doesn't carry them.
function pickTotals(response: ProposalDC | undefined): ProposalTotals | undefined {
  if (!response || Array.isArray(response) || typeof response.price_with_tax !== 'number') {
    return undefined;
  }

  return {
    subtotal_price: response.subtotal_price,
    price_with_discount: response.price_with_discount,
    tax_value: response.tax_value,
    price_with_tax: response.price_with_tax,
  };
}

// Returns the totals along with the number, so a new quote doesn't show a spinner
// until the next stream update.
async function createProposal(
  data: ProposalEntity,
  workspaceId: string
): Promise<{ number: number; totals?: ProposalTotals }> {
  const params = mapProposalEntityToDC(data);

  const response = await axios.post<ProposalDC>(endpoints.proposals.post, omit(params, 'number'), {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });

  return { number: response.data.number, totals: pickTotals(response.data) };
}

// Returns the totals the server recalculated, so lists don't show the old ones
// until the next stream update.
async function updateProposal(
  data: ProposalEntity,
  workspaceId: string
): Promise<ProposalTotals | undefined> {
  const params = mapProposalEntityToDC(data);

  const response = await axios.patch<ProposalDC>(endpoints.proposals.post, params, {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });

  return pickTotals(response?.data);
}

async function setProposalVisibility(
  uuid: string,
  visible: boolean,
  workspaceId: string
): Promise<void> {
  await axios.patch<ProposalDC>(
    endpoints.proposals.patch,
    {
      uuid,
      not_show: !visible,
      updated_at: Math.floor(Date.now() / 1000),
    },
    {
      headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
    }
  );
}

async function hideProposal(uuid: string, workspaceId: string): Promise<void> {
  await setProposalVisibility(uuid, false, workspaceId);
}

async function updateSettings(
  settings: ProposalSettingsEntity,
  workspaceId: string
): Promise<void> {
  const params = mapProposalSettingsEntityToDC(settings);

  await axios.patch(endpoints['proposal-settings'].post, params, {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });
}

async function deleteProposal(uuid: string, workspaceId: string): Promise<void> {
  await axios.delete<true>(endpoints.proposals.delete.replace('{uuid}', uuid), {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });
}

type PdfSignature = string;
type TempPdfUrl = string;

const pdfCache: Record<PdfSignature, TempPdfUrl> = {};

async function generatePdf(data: ProposalEntity): Promise<TempPdfUrl> {
  const params = mapProposalEntityToDC(data);

  const signature = JSON.stringify(params);

  if (pdfCache[signature]) {
    return pdfCache[signature];
  }

  const response = await axios.post(endpoints.proposals.pdf, params, {
    headers: { [RequestHeaders.CONTENT_TYPE]: ContentTypes.APPLICATION_JSON },
    responseType: 'arraybuffer',
  });

  const blob = new Blob([response.data], { type: ContentTypes.APPLICATION_PDF });
  const tempUrl = URL.createObjectURL(blob);

  pdfCache[signature] = tempUrl;

  return tempUrl;
}

// Temporary URL of the signed PDF stored on the quote (LAE-749). 404 when there is none.
async function getSignedPdfLink(uuid: string, workspaceId: string): Promise<TempPdfUrl> {
  const url = endpoints.proposals.signedPdfLink.replace('{uuid}', uuid);
  const response = await axios.get<string | { link: string }>(url, {
    headers: { [RequestHeaders.WORKSPACE_ID]: workspaceId },
  });
  const { data } = response;
  return typeof data === 'string' ? data : data.link;
}

function generateSignLink(uuid: string) {
  const response = axios.get<string>(`/api/v2/price-proposal/${uuid}/generate-sign-link`);
  return from(response).pipe(map((response) => response.data));
}

function getQuoteByHash(hash: string, language?: string) {
  const url = language
    ? `/api/v2/price-proposal/by-hash/${hash}?language=${language}`
    : `/api/v2/price-proposal/by-hash/${hash}`;
  const response = axios.get(url, { responseType: 'blob' });
  return from(response).pipe(map((response) => response.data));
}

function signQuoteByHash(token: string, status: 'ACCEPTED' | 'REJECTED', signatureFile?: File) {
  const formData = new FormData();
  formData.append('status', status);
  if (status === 'ACCEPTED' && signatureFile) {
    formData.append('signature_file', signatureFile);
  }
  const response = axios.post(`/api/v2/price-proposal/sign/${token}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return from(response).pipe(map((response) => response.data));
}

export {
  createProposal,
  updateProposal,
  generatePdf,
  deleteProposal,
  hideProposal,
  setProposalVisibility,
  updateSettings,
  getPrice,
  getSignedPdfLink,
  generateSignLink,
  getQuoteByHash,
  signQuoteByHash
};
